/**
 * Canonical public entity URL helpers (Phase D.4.1).
 *
 * Canonical: /organization|creator|project/:slug (+ /donate, /checkout, /thank-you)
 * Legacy aliases permanently redirect in App.js:
 *   /org/*, /organizations/:slug*, /creators/:slug*, /projects/:slug*
 * Directory indexes (/organizations, /creators, /projects) stay plural.
 */

export function organizationPublicPath(slug) {
  const cleaned = String(slug || "").trim();
  if (!cleaned) return "/organizations";
  return `/organization/${cleaned}`;
}

export function creatorPublicPath(slug) {
  const cleaned = String(slug || "").trim();
  if (!cleaned) return "/creators";
  return `/creator/${cleaned}`;
}

export function projectPublicPath(slug) {
  const cleaned = String(slug || "").trim();
  if (!cleaned) return "/projects";
  return `/project/${cleaned}`;
}

export function entityPublicPath(entityType, slug) {
  const type = String(entityType || "organization").toLowerCase();
  if (type === "creator") return creatorPublicPath(slug);
  if (type === "project") return projectPublicPath(slug);
  return organizationPublicPath(slug);
}

export function entityDonatePath(entityType, slug) {
  return `${entityPublicPath(entityType, slug)}/donate`;
}

export function entityDonateCheckoutPath(entityType, slug) {
  return `${entityDonatePath(entityType, slug)}/checkout`;
}

export function entityDonateThankYouPath(entityType, slug) {
  return `${entityDonatePath(entityType, slug)}/thank-you`;
}

/**
 * Map legacy query-string /donate?entity_* (optionally branded=1) to canonical checkout.
 * @returns {string|null}
 */
export function legacyDonateQueryToCanonical(search) {
  try {
    const params = new URLSearchParams(search || "");
    const entityType = (params.get("entity_type") || "").trim().toLowerCase();
    const slug = (
      params.get("entity_slug") ||
      params.get("slug") ||
      ""
    ).trim();
    if (!entityType || !slug) {
      return null;
    }
    const type = entityType === "org" ? "organization" : entityType;
    if (!["organization", "creator", "project"].includes(type)) {
      return null;
    }
    return entityDonateCheckoutPath(type, slug);
  } catch (e) {
    return null;
  }
}

/**
 * Infer entity type + slug from a canonical donation checkout/thank-you path.
 */
export function entityFromDonatePath(pathname) {
  try {
    const parts = String(pathname || "")
      .replace(/^\//, "")
      .split("/")
      .filter(Boolean);
    if (parts.length < 3) return null;
    const map = {
      organization: "organization",
      organizations: "organization",
      org: "organization",
      creator: "creator",
      creators: "creator",
      project: "project",
      projects: "project",
    };
    const entityType = map[parts[0]];
    const slug = parts[1];
    if (!entityType || !slug || parts[2] !== "donate") {
      return null;
    }
    return { entityType, slug };
  } catch (e) {
    return null;
  }
}
